import React, { ReactNode, useCallback } from 'react'

import { path } from 'ramda'
import { MenuListProps, GroupBase, components } from 'react-select'
import VirtualList, { ItemInfo } from 'react-tiny-virtual-list'
import styled from 'styled-components'

import { SelectOptionInterface } from '@customTypes'

const defaultItemHeight = 40 // px

const Heading = styled.p`
  color: #999;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  margin: 0;
`

const List = styled(VirtualList)`
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  box-sizing: border-box;
`

const getHeadingElement = (heading: string) => <Heading>{heading}</Heading>

export const getMenuList = (shouldVirtualize?: boolean) => {
  const MenuList = <
    Opt extends SelectOptionInterface,
    IsMulti extends boolean,
    Group extends GroupBase<Opt>
  >({
    children,
    maxHeight,
    isMulti,
    ...props
  }: MenuListProps<Opt, IsMulti, Group>) => {
    const items = React.Children.toArray(children)
    const headingIndexes: number[] = []

    const flattenedItems = items.reduce<ReactNode[]>((acc, item) => {
      const groupedItems = path(['props', 'children'], item)
      const heading = path(['props', 'data', 'label'], item)

      if (Array.isArray(groupedItems)) {
        headingIndexes.push(acc.length)

        return [...acc, getHeadingElement(heading as string), ...groupedItems]
      }

      return [...acc, item]
    }, [])

    const itemCount = flattenedItems.length

    const renderItem = useCallback(
      ({ style, index }: ItemInfo) => {
        const item = flattenedItems[index]

        return (
          <div key={index} style={style}>
            {item}
          </div>
        )
      },
      [flattenedItems]
    )

    if (
      shouldVirtualize === false ||
      (shouldVirtualize !== true && itemCount <= 100)
    ) {
      return (
        <components.MenuList isMulti={isMulti} {...props} maxHeight={maxHeight}>
          {children}
        </components.MenuList>
      )
    }

    const childrenHeight = defaultItemHeight * itemCount // px
    const wrapperHeight =
      childrenHeight > maxHeight ? maxHeight : childrenHeight

    const listStyle =
      childrenHeight <= maxHeight
        ? {
            overflow: 'hidden',
          }
        : {}

    const headingSize = 28
    const itemSize = isMulti ? 40 : 34

    const getItemSize = (index: number) =>
      headingIndexes.includes(index) ? headingSize : itemSize

    return (
      <List
        width="100%"
        height={wrapperHeight}
        itemCount={itemCount}
        itemSize={getItemSize}
        renderItem={renderItem}
        style={listStyle}
      />
    )
  }

  return MenuList as typeof components.MenuList
}
